/* Brokerage units for posting a requirement, the same rules as the BBM app's slider (decided 1 Nov 2026) */

/*
 * `requirements.brokerage.value` means different things per transaction, the server's rule in
 * lib/requirements/feed-candidates.ts: for a rental it is DAYS of rent (payout = budget.max * days / 30),
 * for anything else a PERCENTAGE of the budget (payout = budget.max * percent / 100).
 */

export interface BrokerageUnit {
	min: number;
	max: number;
	step: number;
	defaultValue: number;
	/* Labelled stops under a days track; a percentage track labels its two ends instead */
	marks: Array<number>;
}

const DAYS_UNIT: BrokerageUnit = { min: 15, max: 60, step: 1, defaultValue: 30, marks: [15, 30, 60] };

const PERCENT_UNIT: BrokerageUnit = { min: 0.5, max: 10, step: 0.1, defaultValue: 2, marks: [] };

export const isRentBrokerage = (transactionType: string): boolean => transactionType === "rent";

export const brokerageUnit = (transactionType: string): BrokerageUnit =>
	isRentBrokerage(transactionType) ? DAYS_UNIT : PERCENT_UNIT;

/* Snaps a slider value to its unit: whole days, or one decimal of a percent, inside the range */

export const roundBrokerageValue = (value: number, transactionType: string): number => {
	const unit = brokerageUnit(transactionType);
	const clamped = Math.min(unit.max, Math.max(unit.min, value));
	return isRentBrokerage(transactionType) ? Math.round(clamped) : Math.round(clamped * 10) / 10;
};

export const isBrokerageValueValid = (value: number, transactionType: string): boolean => {
	const unit = brokerageUnit(transactionType);
	return Number.isFinite(value) && value >= unit.min && value <= unit.max;
};

/* "2.5%" (one decimal, a trailing ".0" dropped) or "30 days" */

export const brokerageValueLabel = (value: number, transactionType: string): string => {
	if (isRentBrokerage(transactionType)) return `${Math.round(value)} days`;
	const rounded = Math.round(value * 10) / 10;
	return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
};

/* The wording wherever an offered brokerage is shown: "2.5% of property value" or "30 days of rent" */

export const brokerageBasisLabel = (value: number, transactionType: string): string =>
	isRentBrokerage(transactionType)
		? `${brokerageValueLabel(value, transactionType)} of rent`
		: `${brokerageValueLabel(value, transactionType)} of property value`;

/* What the brokerage comes to in rupees at the top of the budget, rounded to whole rupees */

export const brokeragePayout = (budgetMax: number, value: number, transactionType: string): number =>
	Math.round(((budgetMax || 0) * (value || 0)) / (isRentBrokerage(transactionType) ? 30 : 100));

/* Switching brokerage on starts on the unit's default; switching it off clears the value */

export const brokerageOnToggle = (checked: boolean, transactionType: string) => ({
	is_offering_brokerage: checked,
	value: checked ? brokerageUnit(transactionType).defaultValue : 0,
});

/*
 * Moving between rent and any other transaction while brokerage is on resets the value to the new
 * unit's default, so 2 % is never read as 2 days. Returns the brokerage unchanged otherwise.
 */

export const brokerageOnTransactionChange = (
	brokerage: { is_offering_brokerage: boolean; value: number },
	previousType: string,
	nextType: string,
) =>
	brokerage.is_offering_brokerage && isRentBrokerage(previousType) !== isRentBrokerage(nextType)
		? { ...brokerage, value: brokerageUnit(nextType).defaultValue }
		: brokerage;
